import { useCreateReport } from "@/hooks/use-create-report";
import { selectSheetForElement } from "@/modes/mode-selectors";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import {
  isLightweightDataSetCapture,
  isLightweightImg2d,
  isLightweightMarkupOrExternalMarkup,
  isLightweightNodesGroup,
  isLightweightSingleScanSection,
} from "@/store/lightweight-elements-discriminators";
import {
  selectAncestorArea,
  selectLightweightAncestor,
  selectLightweightChild,
  selectLightweightChildren,
  selectLightweightDirectChildren,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import {
  LightweightElement,
  type LightweightElementsState,
} from "@/store/lightweight-elements-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectCanUseAiIndexing } from "@/store/subscription-features/subscription-features-selectors";
import { selectVideoWalksToLoad } from "@/tools/create-report/create-report-selectors";
import type { OnlyPdfReportStore } from "@/tools/create-report/pdf-report-slice";
import {
  Alert,
  blue,
  CircularProgress,
  Dropdown,
  FaroButton,
  FaroCheckbox,
  FaroDialog,
  FaroDialogProps,
  FaroIconButton,
  FaroRadio,
  FaroRadioGroup,
  FaroText,
  FaroTooltip,
  InfoIcon,
  grey,
  Option,
} from "@faro-lotv/flat-ui";
import { useLocalize, useLocalizeLanguage } from "@faro-lotv/foreign-observers";
import {
  assert,
  GUID,
  validateEnumValue,
  type Required,
} from "@faro-lotv/foundation";
import {
  IElementImg360,
  IElementType,
  IElementTypeHint,
  isIElementGenericImgSheet,
} from "@faro-lotv/ielement-types";
import { selectChildrenDepthFirst } from "@faro-lotv/project-source";
import {
  Img360Mode,
  ReportDateFormat,
  ReportSheetContent,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Divider } from "@mui/material";
import Stack from "@mui/material/Stack";
import { createSelector } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState, type JSX, type ReactNode } from "react";
import { updateProject } from "../common/project-provider/update-project";
import { BetaBadge } from "./beta-badge";
import { useCaptureTreeContext } from "./tree/capture-tree/capture-tree-context";

type ReportConfigurationDialogProps = Pick<
  FaroDialogProps,
  "open" | "onCancel" | "onClose"
>;

/**
 * @returns The dialog containing the options to configure a PDF report
 */
export function ReportConfigurationDialog({
  open,
  ...props
}: ReportConfigurationDialogProps): JSX.Element | null {
  const [includeHyperlinks, setIncludeHyperlinks] = useState(true);
  const [includePageNumbers, setIncludePageNumbers] = useState(true);
  const [includeOverviewPage, setIncludeOverviewPage] = useState(true);
  const [includeTags, setIncludeTags] = useState(true);
  const [includeAnnotations, setIncludeAnnotations] = useState(false);
  const [includeAiDescriptions, setIncludeAiDescriptions] = useState(false);
  const [img360Mode, setImg360Mode] = useState<Img360Mode>(
    Img360Mode.CubeMapFull,
  );
  const [pdfQuality, setPdfQuality] = useState(PdfQuality.web);
  const appLanguage = useLocalizeLanguage();
  const [locale, setLocale] = useState(appLanguage.code);
  const [dateFormat, setDateFormat] = useState(
    ReportDateFormat.monthNameDayYear,
  );

  // Unmount everything when the dialog is closed
  if (!open) {
    return null;
  }

  return (
    <DialogWithLoader
      includeHyperlinks={includeHyperlinks}
      setIncludeHyperlinks={setIncludeHyperlinks}
      includePageNumbers={includePageNumbers}
      setIncludePageNumbers={setIncludePageNumbers}
      includeOverviewPage={includeOverviewPage}
      setIncludeOverviewPage={setIncludeOverviewPage}
      includeTags={includeTags}
      setIncludeTags={setIncludeTags}
      includeAnnotations={includeAnnotations}
      setIncludeAnnotations={setIncludeAnnotations}
      includeAiDescriptions={includeAiDescriptions}
      setIncludeAiDescriptions={setIncludeAiDescriptions}
      img360Mode={img360Mode}
      setImg360Mode={setImg360Mode}
      pdfQuality={pdfQuality}
      setPdfQuality={setPdfQuality}
      locale={locale}
      setLocale={setLocale}
      dateFormat={dateFormat}
      setDateFormat={setDateFormat}
      open={open}
      {...props}
    />
  );
}

type DialogWithLoaderProps = {
  includeHyperlinks: boolean;
  setIncludeHyperlinks(include: boolean): void;
  includePageNumbers: boolean;
  setIncludePageNumbers(include: boolean): void;
  includeOverviewPage: boolean;
  setIncludeOverviewPage(include: boolean): void;
  includeTags: boolean;
  setIncludeTags(include: boolean): void;
  includeAnnotations: boolean;
  setIncludeAnnotations(include: boolean): void;
  includeAiDescriptions: boolean;
  setIncludeAiDescriptions(include: boolean): void;
  img360Mode: Img360Mode;
  setImg360Mode(mode: Img360Mode): void;
  pdfQuality: PdfQuality;
  setPdfQuality(quality: PdfQuality): void;
  locale: string;
  setLocale(locale: string): void;
  dateFormat: ReportDateFormat;
  setDateFormat(dateFormat: ReportDateFormat): void;
} & ReportConfigurationDialogProps;

function DialogWithLoader({
  includeHyperlinks,
  setIncludeHyperlinks,
  includePageNumbers,
  setIncludePageNumbers,
  includeOverviewPage,
  setIncludeOverviewPage,
  includeTags,
  setIncludeTags,
  includeAnnotations,
  setIncludeAnnotations,
  includeAiDescriptions,
  setIncludeAiDescriptions,
  img360Mode,
  setImg360Mode,
  pdfQuality,
  setPdfQuality,
  locale,
  setLocale,
  dateFormat,
  setDateFormat,
  ...props
}: DialogWithLoaderProps): JSX.Element | null {
  const { selectedLeaves } = useCaptureTreeContext();

  const dispatch = useAppDispatch();

  const isAiFeaturesEnabled = useAppSelector(
    (state) =>
      selectHasFeature(Features.AiChat)(state) && selectCanUseAiIndexing(state),
  );

  const { availableLanguages } = useLocalize();
  const languageOptions: Option[] = useMemo(
    () =>
      availableLanguages.map((lang) => ({
        key: lang.code,
        value: lang.code,
        label: lang.name,
      })),
    [availableLanguages],
  );

  const { projectApiClient } = useApiClientContext();

  // Load all the video walks dataset in all the areas.
  const videoWalksToLoad = useAppSelector(
    selectVideoWalksToLoad(selectedLeaves),
  );
  const [initialList] = useState(videoWalksToLoad);
  useEffect(() => {
    if (!initialList.length) return;

    const controller = new AbortController();

    async function loadDataset(): Promise<void> {
      if (!initialList.length) return;

      await dispatch(
        updateProject({
          projectApi: projectApiClient,
          iElementQuery: {
            signal: controller.signal,
            ancestorIds: initialList,
          },
        }),
      );
    }
    loadDataset();

    return () => controller.abort();
  }, [dispatch, projectApiClient, initialList]);

  // Collect the 2d and 360 images to include in the report
  const { content, img2dCount, img360Count, scan360Count } = useAppSelector(
    (state) => selectReportContent(state, selectedLeaves, includeAnnotations),
  );

  const { createReport, isProcessing } = useCreateReport({
    selectedLeaves,
    content,
    img2dCount,
    img360Count,
    scan360Count,
    includeHyperlinks,
    includePageNumbers,
    includeOverviewPage,
    includeTags,
    includeAnnotations,
    includeAiDescriptions,
    img360Mode,
    pdfQuality,
    locale,
    dateFormat,
  });

  const isLoadingVideoData = !!videoWalksToLoad.length;

  return (
    <FaroDialog
      title="Create report"
      disabled={isProcessing || (!img360Count && !img2dCount)}
      {...props}
    >
      <Stack aria-label="configure report dialog" sx={{ gap: 3 }}>
        <Alert variant="neutral" title="Overview" sx={{ color: grey[600] }}>
          Selected locations: {selectedLeaves.length}
        </Alert>
        <Stack>
          <FaroText variant="labelM" sx={{ color: grey[600] }}>
            Select content to include
          </FaroText>
          <FaroCheckbox
            label="Tags"
            controlSx={{ m: 0 }}
            checked={includeTags}
            onClick={() => setIncludeTags(!includeTags)}
          />
          <Stack direction="row" sx={{ alignItems: "center" }}>
            <FaroCheckbox
              label="Links to project"
              controlSx={{ m: 0, flex: 1 }}
              checked={includeHyperlinks}
              onClick={() => setIncludeHyperlinks(!includeHyperlinks)}
            />
            <InfoTooltip title="Adds location links and annotation titles. (For annotation links, mark the Annotations checkbox.)" />
          </Stack>
          <FaroCheckbox
            label="Page numbers"
            controlSx={{ m: 0 }}
            checked={includePageNumbers}
            onClick={() => setIncludePageNumbers(!includePageNumbers)}
          />
          <FaroCheckbox
            label="Overview page"
            controlSx={{ m: 0 }}
            checked={includeOverviewPage}
            onClick={() => setIncludeOverviewPage(!includeOverviewPage)}
          />
          <FaroCheckbox
            label={
              <FaroText variant="bodyM">
                Annotations{" "}
                <FaroText variant="placeholder">
                  (includes saved measurements)
                </FaroText>
              </FaroText>
            }
            controlSx={{ m: 0 }}
            checked={includeAnnotations}
            onClick={() => setIncludeAnnotations(!includeAnnotations)}
          />
          {isAiFeaturesEnabled && (
            <FaroCheckbox
              label={
                <Stack direction="row" sx={{ gap: 1 }}>
                  Include AI Descriptions
                  <BetaBadge />
                </Stack>
              }
              controlSx={{ m: 0 }}
              checked={includeAiDescriptions}
              onClick={() => setIncludeAiDescriptions(!includeAiDescriptions)}
            />
          )}
        </Stack>

        <Divider />

        <Stack>
          <FaroRadioGroup
            disabled={img360Count === 0}
            value={img360Mode}
            onChange={(v) => {
              if (isImg360Mode(v.target.value)) {
                setImg360Mode(v.target.value);
              }
            }}
            label={
              <FaroText variant="labelM" sx={{ color: grey[600] }}>
                360° photos layout
              </FaroText>
            }
          >
            <FaroRadio
              value={Img360Mode.CubeMapFull}
              label="Individual images for the six directions"
              disabled={img360Count === 0}
            />
            <FaroRadio
              value={Img360Mode.CubeMapWalls}
              label={
                <FaroText variant="bodyM">
                  Individual images for the four directions{" "}
                  <FaroText variant="placeholder">
                    (no top and bottom images)
                  </FaroText>
                </FaroText>
              }
              disabled={img360Count === 0}
            />
            <FaroRadio
              value={Img360Mode.Equirectangular}
              label="Flattened 360° photo"
              disabled={img360Count === 0}
            />
          </FaroRadioGroup>
        </Stack>
        <Stack>
          <FaroRadioGroup
            value={pdfQuality}
            onChange={(ev) => {
              if (validateEnumValue(ev.target.value, PdfQuality)) {
                setPdfQuality(ev.target.value);
              }
            }}
            label={
              <Stack direction="row" sx={{ alignItems: "center" }}>
                <FaroText variant="labelM" sx={{ color: grey[600], flex: 1 }}>
                  PDF image quality
                </FaroText>
                <InfoTooltip
                  title={
                    <>
                      Controls image resolution in the exported PDF. <br />
                      Web is optimized for screen use, Print for high-quality
                      output.
                    </>
                  }
                />
              </Stack>
            }
          >
            <FaroRadio
              value={PdfQuality.web}
              label={
                <FaroText variant="bodyM">
                  Web{" "}
                  <FaroText variant="placeholder">(smaller file size)</FaroText>
                </FaroText>
              }
            />
            <FaroRadio
              value={PdfQuality.print}
              label={
                <FaroText variant="bodyM">
                  Print{" "}
                  <FaroText variant="placeholder">(higher quality)</FaroText>
                </FaroText>
              }
            />
          </FaroRadioGroup>
        </Stack>
        <Dropdown
          label={
            <FaroText variant="labelM" sx={{ color: grey[600] }}>
              Language
            </FaroText>
          }
          options={languageOptions}
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
        />

        <Dropdown
          label={
            <FaroText variant="labelM" sx={{ color: grey[600] }}>
              {DATE_FORMAT_LABEL}
            </FaroText>
          }
          options={getDateFormatOptions(locale)}
          value={dateFormat}
          shouldCapitalize={false}
          onChange={(e) => {
            if (validateEnumValue(e.target.value, ReportDateFormat)) {
              setDateFormat(e.target.value);
            }
          }}
        />
        <Stack
          sx={{
            gap: 1,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            // Emulate the sticky actions footer of a regular dialog, to be able to display the loading spinner.
            background: grey[0],
            position: "sticky",
            bottom: 0,
            pt: 1,
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              height: "16px",
            }}
          >
            {isLoadingVideoData && (
              <>
                <CircularProgress
                  size={16}
                  sx={{
                    color: blue[500],
                    width: "16px",
                    height: "16px",
                    overflow: "hidden",
                  }}
                />
                <FaroText variant="bodyS">Loading selected data...</FaroText>
              </>
            )}
          </Stack>

          <FaroTooltip
            title={
              isLoadingVideoData
                ? "Loading the selected data to generate the report. This will only take a moment."
                : null
            }
          >
            <FaroButton
              sx={{ alignSelf: "flex-end" }}
              onClick={createReport}
              isLoading={isProcessing}
              disabled={isLoadingVideoData}
            >
              Create report
            </FaroButton>
          </FaroTooltip>
        </Stack>
      </Stack>
    </FaroDialog>
  );
}

type InfoTooltipProps = {
  /** Content of the info tooltip */
  title: ReactNode;
};

/** @returns an info icon with a tooltip on hover, fitting the styles of the form */
function InfoTooltip({ title }: InfoTooltipProps): JSX.Element {
  return (
    <FaroTooltip title={title}>
      <FaroIconButton size="xs">
        <InfoIcon sx={{ fontSize: 18 }} />
      </FaroIconButton>
    </FaroTooltip>
  );
}

/** This helper type avoids type-assertions, as we want to work with newly added properties, instead of deprecated ones. */
type ReportSheetContentWithAnnotations = Required<
  ReportSheetContent,
  "img360SectionEntries" | "img2dEntries" | "ppcAnnotationIds"
>;

function getOrCreateSheetContent(
  contentsBySheet: Map<GUID, ReportSheetContentWithAnnotations>,
  sheetId: GUID,
): ReportSheetContentWithAnnotations {
  let content = contentsBySheet.get(sheetId);
  if (!content) {
    content = {
      imgSheetId: sheetId,
      img360SectionEntries: [],
      img2dEntries: [],
      ppcAnnotationIds: [],
    };
    contentsBySheet.set(sheetId, content);
  }

  return content;
}

const selectReportContent = createSelector(
  [
    selectLightweightElementsSlice,
    selectIElementsSlice,
    (state: OnlyPdfReportStore) => state.pdfReport.selectedSheetByArea,
    (_state: unknown, ids: GUID[]) => ids,
    (_state: unknown, _ids: GUID[], includeAnnotations: boolean) =>
      includeAnnotations,
  ],
  (
    lightweightElements,
    iElements,
    selectedSheetByArea,
    ids,
    includeAnnotations,
  ) => {
    const contentsBySheet = new Map<GUID, ReportSheetContentWithAnnotations>();
    let scan360Count = 0;

    for (const id of ids) {
      const element = lightweightElements.lightweightElements[id];
      if (!element) continue;

      // Get the image sheet element to which this id corresponds to
      const area = selectAncestorArea(id)({ iElements, lightweightElements });

      // Use the selected sheet for this area from the PDF report state, otherwise fall back to the best sheet
      const selectedSheetId = area ? selectedSheetByArea[area.id] : undefined;
      const sheet = selectedSheetId
        ? selectChildrenDepthFirst(
            area,
            isIElementGenericImgSheet,
          )({ iElements }).find((s) => s.id === selectedSheetId)
        : undefined;
      const resolvedSheet =
        sheet ??
        selectSheetForElement(area)({
          iElements,
        });
      if (!resolvedSheet) continue;

      if (isLightweightImg2d(element)) {
        getOrCreateSheetContent(
          contentsBySheet,
          resolvedSheet.id,
        ).img2dEntries.push({
          id: element.id,
          annotationIds: selectAnnotationIds(
            lightweightElements,
            element.id,
            includeAnnotations,
          ),
        });
        continue;
      }

      // If the selected element is a video mode trajectory,
      // we include the rooms generated by the ProjectAPI from it, by looking the Img360
      // of the odometry path and by reading the targetId property.
      if (
        element.type === IElementType.section &&
        element.typeHint === IElementTypeHint.dataSession
      ) {
        const lightweightVideoMode = selectLightweightChild(
          element,
          (e): e is LightweightElement =>
            e.type === IElementType.section &&
            e.typeHint === IElementTypeHint.odometryPath,
        )({ lightweightElements });

        if (!lightweightVideoMode) continue;

        const videoMode = iElements.iElements[lightweightVideoMode.id];
        if (!videoMode) continue;

        const video360s = selectChildrenDepthFirst(
          videoMode,
          (e): e is IElementImg360 & { targetId: string } =>
            "targetId" in e && !!e.targetId,
        )({ iElements });
        getOrCreateSheetContent(
          contentsBySheet,
          resolvedSheet.id,
        ).img360SectionEntries.push(
          ...video360s.map((v) => ({
            id: v.targetId,
            annotationIds: selectAnnotationIds(
              lightweightElements,
              v.targetId,
              includeAnnotations,
            ),
          })),
        );
      } else {
        getOrCreateSheetContent(
          contentsBySheet,
          resolvedSheet.id,
        ).img360SectionEntries.push({
          id,
          annotationIds: selectAnnotationIds(
            lightweightElements,
            element.id,
            includeAnnotations,
          ),
        });
        if (isLightweightSingleScanSection(element)) {
          scan360Count++;
        }
      }

      if (!includeAnnotations) {
        // If the report should not include annotations, we don't need to add the ppc annotations.
        continue;
      }

      // Accumulate ppc annotations from all relevant DataSetCapture elements,
      // deduplicating by ID to handle multiple 360s on the same sheet with different parents.
      const ppcAnnotations = selectDataSetCaptureAnnotationsOfScan360(
        { lightweightElements },
        element.id,
      );

      if (ppcAnnotations.length) {
        const sheetContent = getOrCreateSheetContent(
          contentsBySheet,
          resolvedSheet.id,
        );
        const existingIds = new Set(sheetContent.ppcAnnotationIds);
        const newAnnotations = ppcAnnotations.filter(
          (id) => !existingIds.has(id),
        );
        sheetContent.ppcAnnotationIds = [
          ...sheetContent.ppcAnnotationIds,
          ...newAnnotations,
        ];
      }
    }

    const content = [...contentsBySheet.values()].map((content) => {
      const area = selectAncestorArea(content.imgSheetId)({
        iElements,
        lightweightElements,
      });

      if (!area) {
        return content;
      }

      return {
        ...content,
        annotationIds: selectAnnotationIds(
          lightweightElements,
          // Only annotations that are associated with the area directly, and not a specific element should be shown on the sheet.
          selectLightweightDirectChildren(
            area,
            isLightweightNodesGroup,
          )({ lightweightElements })[0]?.id,
          includeAnnotations,
        ),
      };
    });
    const img2dCount = content.reduce(
      (sum, c) => sum + c.img2dEntries.length,
      0,
    );
    const img360Count = content.reduce(
      (sum, c) => sum + c.img360SectionEntries.length,
      0,
    );

    return { content, img2dCount, img360Count, scan360Count };
  },
);

const selectDataSetCaptureAnnotationsOfScan360 = createSelector(
  [
    selectLightweightElementsSlice,
    (_state: unknown, elementId: GUID) => elementId,
  ],

  (lightweightElements, elementId) => {
    const element = lightweightElements.lightweightElements[elementId];

    if (!element) return [];

    const dataSetCapture = selectLightweightAncestor(
      element,
      isLightweightDataSetCapture,
    )({ lightweightElements });
    if (!dataSetCapture) return [];

    // The annotations for a given dataset capture are stored in the nodes group,
    // which is a direct child of the dataset capture.
    const nodesGroup = selectLightweightDirectChildren(
      dataSetCapture,
      isLightweightNodesGroup,
    )({ lightweightElements });
    if (nodesGroup.length === 0) return [];

    assert(nodesGroup.length === 1, "Expected exactly one nodes group");

    return selectLightweightChildren(
      nodesGroup[0],
      isLightweightMarkupOrExternalMarkup,
    )(lightweightElements).map((e) => e.id);
  },
);

/**
 * @returns all IDs of markup elements in the lightweight descendants of an element
 * @param lightweightElements current lightweightElements store slice state
 * @param elementId the element to search descendants of
 * @param includeAnnotations if false, an empty array will be returned. Use if report should not contain annotations.
 */
function selectAnnotationIds(
  lightweightElements: LightweightElementsState,
  elementId: GUID,
  includeAnnotations: boolean,
): GUID[] {
  if (!includeAnnotations) return [];
  const element = lightweightElements.lightweightElements[elementId];
  if (!element) return [];
  return selectLightweightChildren(
    element,
    isLightweightMarkupOrExternalMarkup,
  )(lightweightElements).map((e) => e.id);
}

enum PdfQuality {
  /** Compressed images */
  web = "web",
  /** Higher resolution images (=using the useHqImage option of the report generation backend) */
  print = "print",
}

function isImg360Mode(s: string): s is Img360Mode {
  return (
    s === Img360Mode.CubeMapFull ||
    s === Img360Mode.CubeMapWalls ||
    s === Img360Mode.Equirectangular
  );
}

// April 1, 2025
const EXAMPLE_YEAR = 2025;
const EXAMPLE_DATE = new Date(EXAMPLE_YEAR, 3, 1);

function formatExampleDate(format: ReportDateFormat, locale: string): string {
  switch (format) {
    case ReportDateFormat.monthNameDayYear: {
      // This format was chosen to match the backend implementation that generates a localized date.
      const month = EXAMPLE_DATE.toLocaleDateString(locale, { month: "long" });
      const day = EXAMPLE_DATE.getDate();
      const year = EXAMPLE_DATE.getFullYear();
      return `${month} ${day}, ${year}`;
    }
    case ReportDateFormat.monthDayYearSlashes:
      return "04/01/2025";
    case ReportDateFormat.dayMonthYearSlashes:
      return "01/04/2025";
    case ReportDateFormat.yearMonthDayIsoDashes:
      return "2025-04-01";
  }
}

const DATE_FORMAT_LABEL =
  "Date format (for example, April 1 2025 is displayed as follows)";

function getDateFormatOptions(locale: string): Option[] {
  return Object.values(ReportDateFormat).map((format) => ({
    key: format,
    value: format,
    label: formatExampleDate(format, locale),
  }));
}
